/**
 * Warnings — Talent PMS sub-page.
 *
 * Two tabs:
 *   1. Warning Templates   — CRUD for reusable letter templates ({{tokens}}).
 *   2. Warning Letters Log — history of every letter issued, with view /
 *      download / WhatsApp-share buttons.
 *
 * All endpoints under /api/warnings/*. Access-gated via `warnings`
 * permission on the backend.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, Download, Plus, Trash2, Edit3, Send, FileText, Loader2 } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AVAILABLE_TOKENS = [
  { token: '{{rider_name}}', label: 'Rider full name' },
  { token: '{{rider_id}}', label: 'Rider unique ID' },
  { token: '{{platform}}', label: 'Platform (Talabat / Noon / ...)' },
  { token: '{{platform_id}}', label: 'Platform ID' },
  { token: '{{plate}}', label: 'Vehicle plate' },
  { token: '{{reason}}', label: 'Reason (entered when issuing)' },
  { token: '{{additional_notes}}', label: 'Additional notes' },
  { token: '{{date}}', label: 'Issue date' },
  { token: '{{reference_no}}', label: 'Reference no (auto-generated)' },
  { token: '{{issued_by}}', label: 'Issuer name' },
];

const Warnings = ({ user }) => {
  const [tab, setTab] = useState('log');

  // Templates
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const [tplForm, setTplForm] = useState({ title: '', body: '', description: '' });
  const [tplSaving, setTplSaving] = useState(false);

  // Letters
  const [letters, setLetters] = useState([]);
  const [letLoading, setLetLoading] = useState(false);

  // Riders list (for the issue dialog)
  const [riders, setRiders] = useState([]);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueForm, setIssueForm] = useState({
    template_id: '', rider_id: '', reason: '', additional_notes: '',
  });
  const [issuing, setIssuing] = useState(false);
  const [nextRef, setNextRef] = useState('');

  const fetchTemplates = useCallback(async () => {
    setTplLoading(true);
    try {
      const r = await axios.get(`${API}/warnings/templates`, { headers: AUTH() });
      setTemplates(r.data.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setTplLoading(false);
    }
  }, []);

  const fetchLetters = useCallback(async () => {
    setLetLoading(true);
    try {
      const r = await axios.get(`${API}/warnings/letters`, { headers: AUTH() });
      setLetters(r.data.letters || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load letters');
    } finally {
      setLetLoading(false);
    }
  }, []);

  const fetchRiders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/riders`, { headers: AUTH() });
      setRiders(r.data || []);
    } catch (e) {
      // silent — not critical for the log tab
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchLetters();
    fetchRiders();
  }, [fetchTemplates, fetchLetters, fetchRiders]);

  // ---------------- Template handlers ----------------

  const openNewTemplate = () => {
    setEditingTpl(null);
    setTplForm({ title: '', body: '', description: '' });
    setTplDialogOpen(true);
  };

  const openEditTemplate = (tpl) => {
    setEditingTpl(tpl);
    setTplForm({
      title: tpl.title || '',
      body: tpl.body || '',
      description: tpl.description || '',
    });
    setTplDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!tplForm.title.trim() || !tplForm.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setTplSaving(true);
    try {
      if (editingTpl) {
        await axios.put(`${API}/warnings/templates/${editingTpl.id}`, tplForm, { headers: AUTH() });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/warnings/templates`, tplForm, { headers: AUTH() });
        toast.success('Template created');
      }
      setTplDialogOpen(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setTplSaving(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.title}"? This won't affect already-issued letters.`)) return;
    try {
      await axios.delete(`${API}/warnings/templates/${tpl.id}`, { headers: AUTH() });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const insertToken = (token) => {
    setTplForm((f) => ({ ...f, body: (f.body || '') + token }));
  };

  // ---------------- Issue letter ----------------

  const openIssueDialog = async () => {
    setIssueForm({ template_id: '', rider_id: '', reason: '', additional_notes: '' });
    setIssueDialogOpen(true);
    try {
      const r = await axios.get(`${API}/warnings/next-reference-no`, { headers: AUTH() });
      setNextRef(r.data.reference_no || '');
    } catch (e) { /* silent */ }
  };

  const submitIssueLetter = async () => {
    if (!issueForm.template_id || !issueForm.rider_id) {
      toast.error('Please pick a template and a rider');
      return;
    }
    setIssuing(true);
    try {
      const r = await axios.post(
        `${API}/warnings/letters`,
        issueForm,
        { headers: AUTH() }
      );
      toast.success(`Warning letter ${r.data.reference_no} issued`);
      setIssueDialogOpen(false);
      fetchLetters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to issue letter');
    } finally {
      setIssuing(false);
    }
  };

  const viewPdf = (letterId) => {
    const url = `${API}/warnings/letters/${letterId}/pdf?token=${localStorage.getItem('token')}`;
    // Use fetch + blob so the Authorization header works reliably
    axios.get(`${API}/warnings/letters/${letterId}/pdf`, {
      responseType: 'blob', headers: AUTH(),
    }).then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
    }).catch(() => toast.error('Failed to open PDF'));
  };

  const downloadPdf = (letter) => {
    axios.get(`${API}/warnings/letters/${letter.id}/pdf?download=1`, {
      responseType: 'blob', headers: AUTH(),
    }).then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `warning_${letter.reference_no}.pdf`;
      a.click();
      URL.revokeObjectURL(objectUrl);
    }).catch(() => toast.error('Failed to download PDF'));
  };

  const sendWhatsapp = async (letter) => {
    // Pre-flight: fetch the exact number this rider would receive on
    // (SIM → mobile_uae → personal) so the operator sees it before
    // confirming. Aborts cleanly if no number is on file.
    let phone = null;
    let optedIn = false;
    if (letter?.rider_id) {
      try {
        const p = await axios.get(
          `${API}/whatsapp/preview-recipient/${letter.rider_id}`,
          { headers: AUTH() },
        );
        phone = p.data?.phone;
        optedIn = !!p.data?.opted_in;
      } catch (_e) { /* fall through */ }
    }
    if (!phone) {
      toast.error(`Cannot send — no WhatsApp number on file for ${letter?.rider_name || 'this rider'}.`);
      return;
    }
    const optInNote = optedIn ? '' : '\n\nℹ Number will be auto opted-in on send.';
    if (!window.confirm(
      `⚠ Send WARNING LETTER (${letter.reference_no}) via WhatsApp to ${letter.rider_name} on the mobile no. ${phone}?\n\n` +
      `The rider will receive the formal warning-letter PDF (${letter?.warning_type || 'Warning'}). Confirm to proceed.${optInNote}`
    )) return;
    try {
      const r = await axios.post(
        `${API}/warnings/letters/${letter.id}/whatsapp`,
        {}, { headers: AUTH() }
      );
      toast.success(r.data.message || 'Warning letter queued via WhatsApp');
      fetchLetters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'WhatsApp send failed');
    }
  };

  // ---------------- Render ----------------

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="warnings-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Warnings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Standard warning-letter templates & issued letters log.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={openIssueDialog}
            data-testid="issue-warning-btn"
            className="gap-2"
          >
            <Send className="w-4 h-4" /> Issue Warning Letter
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="log" data-testid="tab-log">
            Letters Log ({letters.length})
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            Templates ({templates.length})
          </TabsTrigger>
        </TabsList>

        {/* --------------- Letters Log tab --------------- */}
        <TabsContent value="log" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Issued Letters</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchLetters}>Refresh</Button>
            </CardHeader>
            <CardContent>
              {letLoading ? (
                <div className="flex items-center justify-center py-10 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                </div>
              ) : letters.length === 0 ? (
                <p className="text-center text-slate-500 py-10">
                  No warning letters issued yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Ref No</TableHead>
                        <TableHead>Rider ID</TableHead>
                        <TableHead>Rider Name</TableHead>
                        <TableHead>Platform</TableHead>
                        <TableHead>Platform ID</TableHead>
                        <TableHead>Warning Type</TableHead>
                        <TableHead>Issued By</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {letters.map((l) => (
                        <TableRow key={l.id} data-testid={`letter-row-${l.id}`}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {l.issued_at ? new Date(l.issued_at).toLocaleDateString('en-GB') : '-'}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{l.reference_no}</TableCell>
                          <TableCell className="font-mono text-xs">{l.rider_unique_id || '-'}</TableCell>
                          <TableCell>{l.rider_name}</TableCell>
                          <TableCell>{l.platform || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{l.platform_id || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{l.template_title}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {l.issued_by_name || l.issued_by}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button
                              variant="ghost" size="icon"
                              title="View PDF"
                              onClick={() => viewPdf(l.id)}
                              data-testid={`view-pdf-${l.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              title="Download PDF"
                              onClick={() => downloadPdf(l)}
                              data-testid={`download-pdf-${l.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              title="Send via WhatsApp"
                              onClick={() => sendWhatsapp(l)}
                              data-testid={`whatsapp-${l.id}`}
                            >
                              <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* --------------- Templates tab --------------- */}
        <TabsContent value="templates" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Warning Templates</CardTitle>
              <Button onClick={openNewTemplate} className="gap-2" data-testid="new-template-btn">
                <Plus className="w-4 h-4" /> New Template
              </Button>
            </CardHeader>
            <CardContent>
              {tplLoading ? (
                <div className="flex items-center justify-center py-10 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                </div>
              ) : templates.length === 0 ? (
                <p className="text-center text-slate-500 py-10">
                  No templates yet — click <b>New Template</b> to add one.
                </p>
              ) : (
                <div className="space-y-2">
                  {templates.map((tpl) => (
                    <div
                      key={tpl.id}
                      className="p-3 border rounded-md flex items-start justify-between gap-3"
                      data-testid={`tpl-row-${tpl.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="font-semibold text-slate-800">{tpl.title}</span>
                          {!tpl.is_active && <Badge variant="secondary">Inactive</Badge>}
                        </div>
                        {tpl.description && (
                          <p className="text-xs text-slate-500 mt-1">{tpl.description}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-2 line-clamp-2">
                          {tpl.body.slice(0, 200)}{tpl.body.length > 200 ? '…' : ''}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => openEditTemplate(tpl)}
                          data-testid={`edit-tpl-${tpl.id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => deleteTemplate(tpl)}
                          data-testid={`delete-tpl-${tpl.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* --------------- Template dialog --------------- */}
      <Dialog open={tplDialogOpen} onOpenChange={setTplDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTpl ? 'Edit Template' : 'New Warning Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input
                data-testid="tpl-title-input"
                placeholder="e.g. First Warning — Late Attendance"
                value={tplForm.title}
                onChange={(e) => setTplForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description (optional, internal note)</Label>
              <Input
                placeholder="Internal note about when to use this template"
                value={tplForm.description}
                onChange={(e) => setTplForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div>
              <Label>Body *</Label>
              <Textarea
                data-testid="tpl-body-input"
                rows={12}
                placeholder="Write the letter body. Use tokens like {{rider_name}}, {{plate}}, {{reason}}, {{date}}, {{reference_no}}, {{issued_by}} — they'll be replaced when the letter is issued."
                value={tplForm.body}
                onChange={(e) => setTplForm((f) => ({ ...f, body: e.target.value }))}
                className="font-mono text-sm"
              />
            </div>
            <div className="border rounded-md p-3 bg-slate-50">
              <p className="text-xs font-semibold text-slate-700 mb-2">
                Available tokens — click to insert:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {AVAILABLE_TOKENS.map((t) => (
                  <button
                    key={t.token}
                    type="button"
                    className="px-2 py-1 text-xs rounded bg-white border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 font-mono"
                    onClick={() => insertToken(t.token)}
                    title={t.label}
                  >
                    {t.token}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTplDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={saveTemplate}
              disabled={tplSaving}
              data-testid="tpl-save-btn"
            >
              {tplSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingTpl ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------------- Issue letter dialog --------------- */}
      <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Issue Warning Letter</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Reference No (auto): <span className="font-mono">{nextRef || '—'}</span>
            </div>
            <div>
              <Label>Warning Template *</Label>
              <Select
                value={issueForm.template_id}
                onValueChange={(v) => setIssueForm((f) => ({ ...f, template_id: v }))}
              >
                <SelectTrigger data-testid="issue-template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter((t) => t.is_active !== false).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rider *</Label>
              <Select
                value={issueForm.rider_id}
                onValueChange={(v) => setIssueForm((f) => ({ ...f, rider_id: v }))}
              >
                <SelectTrigger data-testid="issue-rider-select">
                  <SelectValue placeholder="Select rider" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {riders.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name} {r.rider_unique_id ? `— ${r.rider_unique_id}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                data-testid="issue-reason-input"
                rows={2}
                placeholder="Brief reason for issuing the warning"
                value={issueForm.reason}
                onChange={(e) => setIssueForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </div>
            <div>
              <Label>Additional Notes (optional)</Label>
              <Textarea
                rows={3}
                placeholder="Any extra context to include in the letter"
                value={issueForm.additional_notes}
                onChange={(e) => setIssueForm((f) => ({ ...f, additional_notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIssueDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submitIssueLetter}
              disabled={issuing}
              data-testid="issue-submit-btn"
            >
              {issuing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Issue Letter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Warnings;
